import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Play, ArrowRight, Zap, Layers, Sparkles, Target } from "lucide-react";

const VIDEOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_6bdcfb20-efe9-42fd-8280-c12a803cfcdd/artifacts/lwn6barr_Homepage%20-%20Made%20with%20Clipchamp.mp4",
    label: "UGC Style",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6bdcfb20-efe9-42fd-8280-c12a803cfcdd/artifacts/e36pmgbi_Homepage%20-%20Made%20with%20Clipchamp%20%282%29.mp4",
    label: "Product Demo",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6bdcfb20-efe9-42fd-8280-c12a803cfcdd/artifacts/i7782w1e_Homepage%20-%20Made%20with%20Clipchamp%20%283%29.mp4",
    label: "Lifestyle Ad",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6bdcfb20-efe9-42fd-8280-c12a803cfcdd/artifacts/0hwpbg2l_Homepage%20-%20Made%20with%20Clipchamp%20%281%29.mp4",
    label: "Brand Video",
  },
];

const STEPS = [
  { num: "01", title: "Paste Product URL", desc: "Drop any e-commerce link. Our AI extracts product details and emotional hooks automatically.", icon: Target },
  { num: "02", title: "Choose Concept", desc: "Pick from 3 AI-generated ad concepts — each with unique hooks, tone, and storylines.", icon: Sparkles },
  { num: "03", title: "Generate Video", desc: "AI creates visuals, motion, voiceover and stitches a scroll-stopping ad ready to publish.", icon: Play },
];

const FEATURES = [
  { icon: Zap, title: "Multiple Formats", desc: "6s to 20s videos optimized for Reels, Shorts & TikTok" },
  { icon: Target, title: "AI Director", desc: "Analyzes your product, crafts hooks, scripts & visual concepts automatically" },
  { icon: Sparkles, title: "State-of-Art AI", desc: "Industry-leading models for best-in-class video output" },
  { icon: Layers, title: "Template Library", desc: "UGC, Hero Shot, Product Video — pick a style and customize in minutes" },
];

const FAQS = [
  {
    q: "What kinds of ads can I create?",
    a: "UGC avatar videos, multi-scene product ads, hero shots, B-roll, plus AI photoshoots — 10s, 20s, 24s formats, all from a single product image.",
  },
  {
    q: "How long does it take to generate one video?",
    a: "Most ads are ready in 2–5 minutes.",
  },
  {
    q: "Do I need any video-editing or design skills?",
    a: "Nope. Pick a template, upload a product image, write one prompt. We handle scripting, voiceover, b-roll, stitching, captions — the whole thing.",
  },
  {
    q: "How does pricing work?",
    a: "Subscription with monthly credits — Beginner is ₹499 for 50 credits, Pro is ₹1,999 for 200 credits. Need more? Top up anytime at ₹10/credit.",
  },
  {
    q: "Can I cancel or change my plan anytime?",
    a: "Yep. Upgrade, downgrade, or cancel instantly. Any unused credits stay yours, even after you leave.",
  },
  {
    q: "Do my videos have a watermark?",
    a: "Free-tier videos get a small \"Made with Prysh AI\" watermark. Every paid plan ships clean, watermark-free downloads.",
  },
  {
    q: "Can I use the videos commercially?",
    a: "100%. Full commercial rights on everything you generate on a paid plan — Meta, YouTube, your website, anywhere you want.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }}>
      {/* ── HEADER ── */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b" style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png" alt="Prysh AI Labs" className="w-9 h-9 object-contain" />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/pricing-info')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.5)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.5)"} data-testid="nav-pricing">Pricing</button>
            <button onClick={() => navigate('/about')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.5)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.5)"} data-testid="nav-about">About</button>
            <button onClick={() => { const el = document.getElementById('faq'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.5)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.5)"} data-testid="nav-faq">FAQ</button>
            <button onClick={() => navigate('/contact')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.5)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.5)"} data-testid="nav-contact">Contact</button>
          </nav>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/login')}
              variant="ghost"
              className="rounded-full px-5 text-sm font-medium border"
              style={{ color: "#1A1A1A", borderColor: "rgba(0,0,0,0.12)" }}
              data-testid="header-login-btn"
            >
              Log in
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              className="rounded-full px-5 text-sm font-medium border-0"
              style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
              data-testid="header-get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative z-10 pt-24 md:pt-28 pb-12 md:pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-12 gap-12 xl:gap-16 items-center">
          {/* Left — Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="xl:col-span-5"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.5rem] font-bold tracking-tight leading-[1.05] mb-8" style={{ color: "#1A1A1A" }}>
              Create Winning{" "}
              <span style={{ color: "#FF5A36" }}>Video Ads</span>{" "}
              with AI in Minutes
            </h1>

            <p className="text-lg leading-relaxed max-w-md mb-12" style={{ color: "rgba(26,26,26,0.5)" }}>
              From product URL to polished video. Our AI Director analyzes your product, crafts compelling concepts, and generates ready-to-publish ads.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Button
                onClick={() => navigate('/signup')}
                size="lg"
                className="rounded-full px-9 py-7 text-base font-semibold gap-2 transition-shadow hover:shadow-[0_8px_30px_rgba(255,90,54,0.25)]"
                style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
                data-testid="hero-create-ad-btn"
              >
                Create Your First Ad
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => navigate('/book-demo')}
                className="rounded-full px-9 py-7 text-base font-medium gap-2 border"
                style={{ color: "#1A1A1A", borderColor: "rgba(0,0,0,0.12)" }}
                data-testid="hero-watch-demo-btn"
              >
                <Play className="w-4 h-4" strokeWidth={2.5} />
                Book a Demo — Get 1 Free Ad
              </Button>
            </div>
          </motion.div>

          {/* Right — Phone-style Video Grid */}
          <div className="xl:col-span-7 relative flex justify-center xl:justify-end">
            <div className="flex gap-3 md:gap-4 items-end">
              {VIDEOS.map((v, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 50 + i * 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                  className={`relative group shrink-0 ${i % 2 === 0 ? "mt-8" : "mt-0"}`}
                  style={{ width: "clamp(100px, 16vw, 160px)" }}
                  data-testid={`showcase-video-${i + 1}`}
                >
                  {/* Phone frame */}
                  <div
                    className="aspect-[9/16] rounded-[24px] overflow-hidden relative transition-transform duration-300 group-hover:scale-[1.03]"
                    style={{
                      backgroundColor: "#FFFFFF",
                      padding: "6px",
                      boxShadow: "0 12px 50px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    }}
                  >
                    {/* Video inside frame */}
                    <div className="w-full h-full rounded-[19px] overflow-hidden relative bg-gray-100">
                      <video
                        src={v.src}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                          <Play className="w-4 h-4 ml-0.5" style={{ color: "#FF5A36" }} fill="#FF5A36" strokeWidth={0} />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Label below phone */}
                  <p className="text-[11px] font-semibold text-center mt-3" style={{ color: "rgba(26,26,26,0.4)" }}>{v.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BEFORE & AFTER ── */}
      <section className="relative z-10 py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{ color: "#FF5A36" }}>The Transformation</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>
              From product photo to polished ad
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center"
          >
            {/* Before */}
            <div className="relative">
              <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: "rgba(26,26,26,0.8)", color: "#FFFFFF" }}>
                Before
              </div>
              <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                <img
                  src="https://prysh-ai-media.s3.ap-south-1.amazonaws.com/homepage/before_product.jpeg"
                  alt="Product photo before"
                  className="w-full aspect-video object-cover"
                />
              </div>
              <p className="text-sm mt-3 text-center" style={{ color: "rgba(26,26,26,0.4)" }}>Raw product image</p>
            </div>

            {/* Arrow connector (desktop only) */}

            {/* After */}
            <div className="relative">
              <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}>
                After
              </div>
              <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(255,90,54,0.15)", boxShadow: "0 4px 20px rgba(255,90,54,0.08)" }}>
                <video
                  src="https://prysh-ai-media.s3.ap-south-1.amazonaws.com/homepage/after_video.mp4"
                  className="w-full aspect-video object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
              </div>
              <p className="text-sm mt-3 text-center" style={{ color: "rgba(26,26,26,0.4)" }}>AI-generated video ad</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="relative z-10 py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section header row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{ color: "#FF5A36" }}>How It Works</p>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>
                Three steps to your next ad
              </h2>
            </div>
          </motion.div>

          {/* Large decorative numbers + content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.12 }}
                className="relative rounded-2xl p-8 md:p-9 border transition-all duration-300 group"
                style={{ backgroundColor: "#FFFFFF", borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,90,54,0.25)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.06)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.03)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {/* Number badge + icon row */}
                <div className="flex items-center justify-between mb-6">
                  <span className="font-display text-3xl font-black" style={{ color: "#FF5A36" }}>
                    {step.num}
                  </span>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(255,90,54,0.07)" }}>
                    <step.icon className="w-5 h-5" style={{ color: "#FF5A36" }} strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="font-display text-xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(26,26,26,0.45)" }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="relative z-10 py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{ color: "#FF5A36" }}>Built for Performance</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>
              Every ad optimized for results
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl p-7 pb-8 border group transition-all duration-300 relative overflow-hidden"
                style={{ backgroundColor: "rgba(255,255,255,0.65)", borderColor: "rgba(0,0,0,0.06)" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,90,54,0.25)"; e.currentTarget.style.backgroundColor = "#FFFFFF"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.06)"; e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.65)"; }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(255,90,54,0.07)" }}>
                  <f.icon className="w-5 h-5" style={{ color: "#FF5A36" }} strokeWidth={1.5} />
                </div>
                <h4 className="font-display text-base font-semibold mb-2" style={{ color: "#1A1A1A" }}>{f.title}</h4>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(26,26,26,0.45)" }}>{f.desc}</p>
                {/* Orange accent bar at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-1 transition-all duration-300" style={{ background: "linear-gradient(90deg, #FF5A36, #FF8C42)", opacity: 0, transform: "scaleX(0)" }}
                  ref={el => {
                    if (!el) return;
                    const parent = el.parentElement;
                    parent.addEventListener('mouseenter', () => { el.style.opacity = "1"; el.style.transform = "scaleX(1)"; });
                    parent.addEventListener('mouseleave', () => { el.style.opacity = "0"; el.style.transform = "scaleX(0)"; });
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="relative z-10 py-16 md:py-20 px-6 md:px-12" data-testid="landing-faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-10 md:mb-14 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{ color: "#FF5A36" }}>Frequently asked</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>
              Everything you need to know
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="landing-faq-accordion">
            {FAQS.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
              >
                <AccordionItem
                  value={`item-${i + 1}`}
                  className="rounded-2xl border px-5 md:px-6 transition-colors duration-200 bg-white/65 hover:bg-white"
                  style={{ borderColor: "rgba(0,0,0,0.06)" }}
                  data-testid={`landing-faq-item-${i + 1}`}
                >
                  <AccordionTrigger
                    className="text-left text-base md:text-lg font-medium hover:no-underline py-5 [&[data-state=open]>svg]:text-[#FF5A36]"
                    style={{ color: "#1A1A1A" }}
                    data-testid={`landing-faq-trigger-${i + 1}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-sm md:text-base leading-relaxed pb-5"
                    style={{ color: "rgba(26,26,26,0.55)" }}
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>

        {/* SEO: FAQPage JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD).replace(/</g, "\\u003c") }}
        />
      </section>

      {/* ── CTA BAND ── */}
      <section className="relative z-10 py-16 md:py-20 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto rounded-[2rem] p-14 md:p-20 text-center relative overflow-hidden"
          style={{ backgroundColor: "#1A1A1A" }}
        >
          {/* Inner coral glow */}
          <div className="absolute top-[-50px] right-[-50px] w-[350px] h-[350px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,90,54,0.12) 0%, transparent 70%)" }} />
          <div className="absolute bottom-[-40px] left-[-40px] w-[250px] h-[250px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,140,66,0.08) 0%, transparent 70%)" }} />

          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-6 relative z-10" style={{ color: "#FFFFFF" }}>
            Ready to create your first ad?
          </h2>
          <p className="text-base md:text-lg mb-12 max-w-xl mx-auto relative z-10" style={{ color: "rgba(255,255,255,0.5)" }}>
            Join brands creating scroll-stopping video ads with AI. No video editing skills required.
          </p>
          <Button
            onClick={() => navigate('/signup')}
            size="lg"
            className="rounded-full px-10 py-7 text-base font-semibold gap-2 relative z-10 transition-shadow hover:shadow-[0_8px_30px_rgba(255,90,54,0.3)]"
            style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
            data-testid="cta-get-started-btn"
          >
            Get Started Free
            <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Button>
        </motion.div>
      </section>

      {/* ── SEO CONTENT SECTION — long-form value-prop copy to satisfy search engines ── */}
      <section className="relative z-10 py-24 md:py-32 px-6 md:px-12" style={{ backgroundColor: "#FAFAF7" }}>
        <div className="max-w-5xl mx-auto space-y-16">
          <div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-6" style={{ color: "#1A1A1A" }}>
              The AI ad creative studio built for modern brands
            </h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(26,26,26,0.7)" }}>
              Prysh AI is a full end-to-end platform that helps e-commerce brands, direct-to-consumer marketers, growth
              teams, and independent creators produce high-converting video and image advertisements in minutes instead
              of weeks. From a single product photograph and a short creative brief, Prysh assembles studio-quality
              scenes, on-brand voiceovers, and ready-to-publish exports for Meta, Google, TikTok, Instagram Reels, and
              YouTube Shorts. There is no complicated timeline editor, no expensive shoot day, and no external freelancer
              chain to coordinate — every step from ideation to final render happens inside a single guided workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>Four studios, one creative brain</h3>
              <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.65)" }}>
                Prysh ships four purpose-built studios. The <strong>Marketing Studio</strong> generates polished
                multi-scene video ads using Kling O3 Pro and an AI Director that automatically composes shots from your
                product and avatar images. The <strong>Avatar Studio</strong> lets you browse curated American and
                Indian preset personas or build custom AI avatars from your own reference photos. The{" "}
                <strong>Product Studio</strong> analyzes your product catalog and stores reusable creative briefs. The{" "}
                <strong>Image Studio</strong> unifies four leading text-to-image models — Google Nano Banana 2, Nano
                Banana Pro, OpenAI GPT-Image 1.5, and ByteDance Seedream v5 Lite — behind one prompt bar with configurable
                aspect ratio, resolution, and reference-image controls.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>User-generated content at scale</h3>
              <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.65)" }}>
                UGC-style advertisements consistently outperform traditional polished creative on paid social. Prysh
                makes UGC affordable and repeatable: pick an avatar, upload your product, and Prysh will generate anchor
                images, script the beats, produce spoken dialogue with ElevenLabs voiceovers, and stitch continuous
                jump-cut videos using Sora 2 or Kling. Five distinct UGC formats are supported out of the box, including
                short-form testimonials, story arcs, multi-shot demos, and B-roll compilations. Every generation is
                billed by transparent credit deduction with automatic refunds if any scene fails during rendering.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>Enterprise-grade infrastructure</h3>
              <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.65)" }}>
                Behind every render sits an S3-backed asset pipeline, MongoDB-based project store, background job
                orchestration that survives page reloads, and a Razorpay subscription engine with monthly recurring plans
                for Starter, Pro, and Business tiers. Enterprise customers get dedicated concurrency, custom credit
                allotments, and priority render queues. Every model integration — Google Gemini, OpenAI, Fal.ai,
                ByteDance, ElevenLabs — is treated as a first-class provider with dynamic pricing that admins can tune
                without shipping code.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>Built for shipping, not tinkering</h3>
              <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.65)" }}>
                Every studio inside Prysh is designed around a single question: how quickly can this creative go live?
                Assisted wizards guide first-time creators through avatar selection, product analysis, and story
                composition. Power users can drop into a single-prompt mode and generate variations in bulk. Finished
                videos and images export at broadcast-ready resolutions with proper aspect ratios for every major ad
                platform. Whether you run a solo Shopify store or a performance-marketing agency managing dozens of
                accounts, Prysh compresses weeks of creative production into an afternoon of focused work.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-display text-xl md:text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>Who Prysh AI is for</h3>
            <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.65)" }}>
              E-commerce founders launching new product SKUs. Direct-to-consumer brands scaling paid acquisition on Meta
              and Google. Performance marketers who need daily creative refreshes to fight ad fatigue. Digital agencies
              serving multiple clients with consistent brand output. Content creators building portfolios of spec ads.
              Solo entrepreneurs who cannot justify a full production crew for every campaign. If you spend more than a
              few hours a week thinking about video and image ad creative, Prysh will give you a measurable edge.
            </p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-16 md:py-20 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png" alt="Prysh AI Labs" className="w-8 h-8 object-contain" />
                <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI Labs</span>
              </div>
              <p className="text-sm" style={{ color: "rgba(26,26,26,0.3)" }}>AI Ad Creative Director</p>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => navigate('/pricing-info')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-pricing-link">Pricing</button>
              <button onClick={() => navigate('/about')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-about-link">About</button>
              <button onClick={() => navigate('/contact')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-contact-link">Contact Us</button>
              <button onClick={() => navigate('/terms')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-terms-link">Terms of Use</button>
              <button onClick={() => navigate('/privacy')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-privacy-link">Privacy Policy</button>
              <button onClick={() => navigate('/refund')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-refund-link">Refund Policy</button>
              <button onClick={() => navigate('/login')} className="text-sm font-medium transition-colors" style={{ color: "rgba(26,26,26,0.4)" }} onMouseEnter={e => e.target.style.color = "#1A1A1A"} onMouseLeave={e => e.target.style.color = "rgba(26,26,26,0.4)"} data-testid="footer-login-link">Log in</button>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
            <p className="text-xs" style={{ color: "rgba(26,26,26,0.2)" }}>&copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
